import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { MaterialSelect } from "@/components/slot/MaterialSelect";
import {
  useCreateSlot,
  useUpdateSlot,
  useDeleteSlot,
} from "@/api/hooks/useSloty";
import { useEdgeCandidates, useHrubkyDosiek } from "@/api/hooks/useCennik";
import { useOknoZmenyHran } from "@/components/slot/ZmenaHranSlotuDialog";
import {
  SlotEdgesByThicknessSection,
  jeSkloMaterial,
} from "@/components/slot/HranySlotu";
import { useZakazkaDetail } from "@/api/hooks/useZakazky";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { cn } from "@/lib/utils";
import type { EdgeCandidateRow, Material, MaterialSlot } from "@/api/schemas";
import { cisloMm } from "@/lib/format";
import { FILTER_TLACIDLO, FILTER_ZAPNUTY } from "@/components/vyber/KategoriaVyberu";

/** Druh materiálu v slote — plošný (dosky, sklo) alebo dĺžkový (PD…). */
type SlotDruh = "plosny" | "dlzkovy";

/** Default hrúbka pri otvorení dialógu — 18 mm (najčastejšie v Tandem). */
const DEFAULT_HRUBKA = "18";
/** Sentinel pre "Všetky hrúbky" (žiadny z_mm filter). */
const ALL_HRUBKY = "__all__";
/** Štandardné hrúbky vždy ponúknuté (zlúčia sa s distinct z cenníka). */
const COMMON_HRUBKY = [3, 10, 12, 16, 18, 19, 25, 36, 38];

/** Zlúči common + API hrúbky, deduplikuje, zoradí vzostupne. */
function mergeHrubky(api: string[] | undefined): number[] {
  const set = new Set<number>(COMMON_HRUBKY);
  for (const v of api ?? []) {
    const n = parseFloat(v);
    if (Number.isFinite(n)) set.add(n);
  }
  return [...set].sort((a, b) => a - b);
}

export interface SlotEditDialogProps {
  zakazkaId: string;
  variantaId: string;
  /** Existujúci slot (edit mode) alebo null (create mode). */
  slot: MaterialSlot | null;
  /** Návrh slot_kod pre create mode (M1, M2, …). */
  suggestedKod: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Otvorí kalkulačku skla — zavrie slot dialóg (len create mode). */
  onOpenSklo?: () => void;
  /**
   * Po pridaní materiálu (KOLO 228): okno sa zavrie a panel otvorí okno
   * Hrany nového materiálu. Bez neho sa okno ako do KOLA 228 prepne do
   * úpravy čerstvého slotu.
   */
  onCreated?: (slot: MaterialSlot) => void;
  /** Odkaz z úpravy na okno Hrany toho istého materiálu (KOLO 228). */
  onOtvorHrany?: () => void;
}

export function SlotEditDialog({
  zakazkaId,
  variantaId,
  slot: slotProp,
  suggestedKod,
  open,
  onOpenChange,
  onOpenSklo,
  onCreated,
  onOtvorHrany,
}: SlotEditDialogProps): JSX.Element {
  // Po vytvorení sa dialóg NEzavrie — prepne sa do edit módu čerstvého
  // slotu, aby sa hrany dali hneď doladiť (overrides vyžadujú slot.id).
  const [createdSlot, setCreatedSlot] = useState<MaterialSlot | null>(null);
  const slot = slotProp ?? createdSlot;
  const isEdit = !!slot;
  const createMutation = useCreateSlot(zakazkaId, variantaId);
  const updateMutation = useUpdateSlot(zakazkaId);
  const deleteMutation = useDeleteSlot(zakazkaId);
  // Zmena hrán, ktorá sa prenesie do dielcov — okno so zoznamom (KOLO 222)
  const oknoHran = useOknoZmenyHran(slot?.id ?? "", slot?.slot_kod ?? "");

  const [slotKod, setSlotKod] = useState("");
  const [label, setLabel] = useState("");
  const [materialId, setMaterialId] = useState<string | null>(null);
  // Plošný (dosky, sklo — m²) vs dĺžkový (PD, zásteny, profily — €/m)
  const [druh, setDruh] = useState<SlotDruh>("plosny");
  /**
   * `je_auto_hrany` slotu. **Fajku už nemá** (KOLO 123) — automatika beží
   * vždy a ručné rozhodnutie ju prebíja tam, kde vzniká: v tabuľke hrán
   * podľa hrúbky, ktorá zapisuje `slot_hrana_override`. Príznak sa preto
   * len nesie ďalej: import ho stavia na `false` (hrany prišli z exportu
   * a appka ich nemá prepisovať) a to sa musí zachovať.
   */
  const [autoHrany, setAutoHrany] = useState(true);
  /** Sklo sa neolepuje — sekcia hrán sa preň schová podľa DRUHU materiálu. */
  const [jeSklo, setJeSklo] = useState(false);
  /**
   * Masív sa neolepuje (KOLO 168) — strany sa zaobľujú na olepovačke,
   * sekcia hrán sa schová ako pri skle. Rozhoduje backend
   * (`material.masiv`), zoznam kategórií si UI nedrží (lekcia KOLA 77).
   */
  const [jeMasiv, setJeMasiv] = useState(false);
  // Voľba hrúbky dosky — filter pre material picker (default 18 mm).
  const [zMm, setZMm] = useState<string>(DEFAULT_HRUBKA);

  const hrubkyQuery = useHrubkyDosiek("plosny");
  const hrubky = mergeHrubky(hrubkyQuery.data?.data);
  // Vybraná tenká/hrubá hrana (default = auto, prepínateľné z kandidátov).
  const [tenkaId, setTenkaId] = useState<string | null>(null);
  const [hrubaId, setHrubaId] = useState<string | null>(null);

  // Projektová hrubá hrana (z čítania zákazky) — určuje auto-default hrubej.
  const zakazkaQuery = useZakazkaDetail(zakazkaId);
  const hrubaHranaMm = zakazkaQuery.data?.data?.hruba_hrana_mm
    ? String(zakazkaQuery.data.data.hruba_hrana_mm)
    : undefined;

  const candidatesQuery = useEdgeCandidates(
    // Masív hrany nemá — kandidátov netreba ani načítať
    jeMasiv ? undefined : (materialId ?? undefined),
    hrubaHranaMm,
    slot?.id ?? undefined,
  );
  const candidates: EdgeCandidateRow[] =
    candidatesQuery.data?.data?.candidates ?? [];

  // Sleduje pre ktorý materiál sme naposledy doplnili auto-default —
  // pri zmene materiálu prepíše tenká/hrubá novými auto-pickmi, inak nechá
  // používateľov výber (a v edit móde uložené hodnoty slotu).
  const autoFilledFor = useRef<string | null>(null);

  // Reset formulára LEN pri otvorení dialógu — nie pri každej zmene
  // suggestedKod (ten sa po create zmení M1→M2 refetchom a zhodil by
  // čerstvý edit mód späť na create).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setCreatedSlot(null);
      const s = slotProp;
      setSlotKod(s?.slot_kod ?? suggestedKod);
      setLabel(s?.label ?? "");
      setMaterialId(s?.material_id ?? null);
      setDruh(s?.material?.druh === "dlzkovy" ? "dlzkovy" : "plosny");
      setAutoHrany(s?.je_auto_hrany ?? true);
      setJeSklo(jeSkloMaterial(s?.material?.kategoria));
      setJeMasiv(!!s?.material?.masiv);
      setTenkaId(s?.hrana_tenka_id ?? null);
      setHrubaId(s?.hrana_hruba_id ?? null);
      // Create: default 18 mm. Edit: "Všetky" (vybraný materiál je už uložený).
      setZMm(s ? ALL_HRUBKY : DEFAULT_HRUBKA);
      // Edit s materiálom → nepúšťaj auto-fill (drž uložené); create → pusti.
      autoFilledFor.current = s?.material_id ?? null;
    }
    wasOpen.current = open;
  }, [open, slotProp, suggestedKod]);

  // Po načítaní kandidátov (alebo zmene materiálu) doplň auto-default.
  useEffect(() => {
    const data = candidatesQuery.data?.data;
    if (!open || !data || !materialId) return;
    if (autoFilledFor.current !== materialId) {
      setTenkaId(data.auto_tenka_id ?? null);
      setHrubaId(data.auto_hruba_id ?? null);
      autoFilledFor.current = materialId;
    }
  }, [candidatesQuery.data, materialId, open]);

  function handleDruhChange(d: SlotDruh): void {
    if (d === druh) return;
    setDruh(d);
    setMaterialId(null);
    setTenkaId(null);
    setHrubaId(null);
    autoFilledFor.current = null;
    // Dĺžkové materiály sa neolepujú ABS hranami
    setAutoHrany(d === "plosny");
    setJeSklo(false);
    setJeMasiv(false);
  }

  function handlePickMaterial(id: string | null, m?: Material): void {
    setMaterialId(id);
    // Sklo sa neolepuje — sekcia hrán sa schová a príznak ide na false
    if (id && m) {
      const sklo = jeSkloMaterial(m.kategoria);
      setJeSklo(sklo);
      if (sklo) setAutoHrany(false);
      // Masív tiež nie (KOLO 168) — strany sa zaobľujú na dielci
      setJeMasiv(!!m.masiv);
    } else if (!id) {
      setJeMasiv(false);
    }
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!materialId) {
      toast({
        variant: "destructive",
        title: "Materiál je povinný",
        description: "Vyber materiál zo zoznamu.",
      });
      return;
    }
    try {
      if (isEdit && slot) {
        // Iná doska mení aj dekor hrán dielcov, ktoré idú podľa materiálu
        // (KOLO 222) — najprv okno so zoznamom, PATCH už dosku neposiela
        if (materialId !== slot.material_id) {
          const ulozene = await oknoHran.spusti(
            { material_id: materialId },
            `zmena dosky v materiáli ${slot.slot_kod}`,
          );
          if (!ulozene) return;
        }
        await updateMutation.mutateAsync({
          id: slot.id,
          data: {
            slot_kod: slotKod,
            label: label || null,
            je_auto_hrany: druh === "plosny" && autoHrany,
            // Ulož vybrané hrany (auto-default alebo ručne prepnuté);
            // masív hrany nenesie (KOLO 168)
            hrana_tenka_id: druh === "plosny" && !jeMasiv ? tenkaId : null,
            hrana_hruba_id: druh === "plosny" && !jeMasiv ? hrubaId : null,
          },
        });
        toast({ title: "Materiál uložený" });
      } else {
        const created = await createMutation.mutateAsync({
          slot_kod: slotKod,
          label: label || null,
          material_id: materialId,
          je_auto_hrany: druh === "plosny" && autoHrany,
          hrana_tenka_id: druh === "plosny" && !jeMasiv ? tenkaId : null,
          hrana_hruba_id: druh === "plosny" && !jeMasiv ? hrubaId : null,
        });
        if (onCreated) {
          // Hrany nového materiálu má na starosti okno Hrany (KOLO 228)
          onOpenChange(false);
          onCreated(created.data);
          toast({ title: "Materiál pridaný", description: `${slotKod} — doladiť hrany môžeš tu.` });
          return;
        }
        // Nezatváraj — prepni do edit módu, hrany sa dajú hneď doladiť.
        setCreatedSlot(created.data);
        toast({
          title: "Materiál uložený",
          description: `${slotKod} — môžeš doladiť hrany nižšie.`,
        });
        return;
      }
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  async function handleDelete(): Promise<void> {
    if (!slot) return;
    if (
      !window.confirm(
        `Naozaj odobrať materiál ${slot.slot_kod} zo zákazky? Dielce ho prestanú používať.`,
      )
    ) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(slot.id);
      toast({ title: "Materiál odobratý" });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Materiál ${slot?.slot_kod}` : "Pridať materiál"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Materiál zákazky — doska a hrany, ktoré z neho dielce dedia.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Výber materiálu je PRVÝ (vyhľadávanie hneď pod druhom) —
              Popis je až pod ním, aby sa nemýlil s vyhľadávacím poľom.
              Kód M1/M2/… sa generuje automaticky — neukazujeme. */}
          <div className="space-y-2">
            {/* Druh materiálu — plošný (m²) vs dĺžkový (€/m, len sa kráti) */}
            <div className="flex gap-1 rounded-md border p-1 w-fit">
              {(
                [
                  ["plosny", "Plošný (dosky, sklo)"],
                  ["dlzkovy", "Dĺžkový (prac. dosky, zásteny…)"],
                ] as [SlotDruh, string][]
              ).map(([d, lbl]) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => handleDruhChange(d)}
                  aria-pressed={druh === d}
                  className={cn(
                    "rounded px-2.5 py-1 text-xs transition-colors",
                    druh === d
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent",
                  )}
                >
                  {lbl}
                </button>
              ))}
              {!isEdit && onOpenSklo ? (
                // Vstup do kalkulačky skla — nevytvára slot, vloží sklo
                // s parametrami a cenou priamo do zostavy.
                <button
                  type="button"
                  onClick={() => {
                    onOpenChange(false);
                    onOpenSklo();
                  }}
                  className="rounded px-2.5 py-1 text-xs text-muted-foreground hover:bg-accent transition-colors"
                >
                  Sklo (kalkulačka)
                </button>
              ) : null}
            </div>
            <Label>Materiál</Label>
            <MaterialSelect
              value={materialId}
              onChange={handlePickMaterial}
              druh={druh}
              // Slot je doska alebo pracovná doska — hrany aj kovanie sú
              // tiež dĺžkové, ale majú vlastné flow (auto-hrany,
              // „+ Kovanie"). Sekcia to povie serveru jedným slovom
              // namiesto zoznamu prefixov, ktorý sa rozišiel (krytky
              // a šatníkové tyče v ňom chýbali, KOLO 164).
              sekcia="materialy"
              // Kurzor do hľadania len keď ešte niet čo ukázať — inak by
              // `onFocus` zbalený zoznam hneď zase rozbalil
              autoFocus={!materialId}
              // Zbalený zoznam (KOLO 123): rozbalený je ~450 px cenníka
              // nad tabuľkou hrán, aj keď je materiál dávno vybraný.
              // Prop tu bol od KOLA 83, len ho tento dialóg neuviedol.
              kompaktny
              vysokyZoznam
              zMm={druh === "plosny" && zMm !== ALL_HRUBKY ? zMm : undefined}
              // Hrúbka dosky je jeden filter v riadku filtrov (KOLO 202) —
              // rozbaľovačka s hrúbkami, nie ešte kapsula k tomu istému
              rozmerKapsule={false}
              filtreNavyse={
                druh === "plosny" ? (
                  <Select value={zMm} onValueChange={setZMm}>
                    <SelectTrigger
                      aria-label="Hrúbka dosky"
                      className={cn(FILTER_TLACIDLO, zMm !== ALL_HRUBKY && FILTER_ZAPNUTY)}
                    >
                      <span>
                        {zMm === ALL_HRUBKY ? "Hrúbka dosky" : `Hrúbka: ${cisloMm(zMm)} mm`}
                      </span>
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value={ALL_HRUBKY}>Všetky hrúbky</SelectItem>
                      {hrubky.map((h) => (
                        <SelectItem key={h} value={String(h)}>
                          {cisloMm(String(h))} mm
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : null
              }
              placeholder={
                druh === "dlzkovy"
                  ? "Vyhľadať dĺžkový materiál (pracovná doska, zástena…)"
                  : "Vyhľadať materiál (názov, kód, dekor)…"
              }
            />
          </div>

          <Separator />

          <div className="space-y-1.5">
            <Label htmlFor="slot-label">Popis</Label>
            <Input
              id="slot-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Korpusy / Dvere / Pracovná doska"
            />
            <p className="text-[11px] text-muted-foreground">
              Kód materiálu: <span className="font-mono">{slotKod}</span>
            </p>
          </div>

          <Separator />

          {druh === "dlzkovy" ? (
            <p className="text-xs text-muted-foreground">
              Dĺžkový materiál sa objednáva na metre a pri rezaní sa len
              kráti — hrany sa neriešia. Dielec s týmto materiálom udáva
              dĺžku v X (mm); cena = dĺžka × cena/m.
            </p>
          ) : null}

          <div className={druh === "dlzkovy" ? "hidden" : "space-y-3"}>
            {jeSklo ? (
              <p className="text-xs text-muted-foreground">
                Sklo sa neolepuje ABS hranou — hrany sa preň neriešia.
              </p>
            ) : null}
            {jeMasiv ? (
              <p className="text-xs text-muted-foreground">
                Masív sa neolepuje — strany sa zaobľujú na olepovačke
                (zaoblenie zapneš na dielci).
              </p>
            ) : null}

            {materialId && !jeSklo && !jeMasiv ? (
              slot && materialId === slot.material_id ? (
                // Hrany uloženej dosky sú v okne Hrany (KOLO 228) — tu by
                // boli zase „dole pod výberom", čo user nechcel
                <p className="text-xs text-muted-foreground">
                  Hrany tohto materiálu sú v okne{" "}
                  {onOtvorHrany ? (
                    <button
                      type="button"
                      onClick={onOtvorHrany}
                      className="text-primary hover:underline"
                    >
                      Hrany {slot.slot_kod}
                    </button>
                  ) : (
                    <>Hrany</>
                  )}
                  {" "}— tlačidlo Hrany na karte materiálu.
                </p>
              ) : candidatesQuery.isLoading ? (
                <p className="text-xs text-muted-foreground">Hľadám hrany…</p>
              ) : candidates.length === 0 ? (
                <p className="text-xs text-warning">
                  Pre dekor{" "}
                  <span className="font-mono">
                    {candidatesQuery.data?.data?.material_dekor ?? "?"}
                  </span>{" "}
                  sa v cenníku nenašli žiadne hrany.
                </p>
              ) : (
                <>
                  {/* Náhľad hrán novej dosky pred uložením (KOLO 135) — user
                      chce „!" vidieť hneď po výbere materiálu, nie až na
                      dielci. Meniť sa dajú v okne Hrany. */}
                  {slot ? (
                    <p className="text-[11px] font-medium text-muted-foreground">
                      Hrany novej dosky
                    </p>
                  ) : null}
                  <SlotEdgesByThicknessSection materialId={materialId} />
                  <p className="text-xs text-muted-foreground">
                    Našlo sa {candidates.length} hrán pre tento dekor —{" "}
                    {slot
                      ? "po uložení ich doladíš tlačidlom Hrany na karte materiálu."
                      : "po pridaní sa otvorí okno Hrany, kde ich doladíš."}
                  </p>
                </>
              )
            ) : null}
          </div>

          <DialogFooter className="!justify-between">
            <div>
              {isEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleDelete}
                  className="text-destructive hover:bg-destructive/10"
                >
                  Odobrať materiál
                </Button>
              ) : null}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Zrušiť
              </Button>
              <Button type="submit">
                {isEdit ? "Uložiť" : "Pridať"}
              </Button>
            </div>
          </DialogFooter>
        </form>
        {oknoHran.okno}
      </DialogContent>
    </Dialog>
  );
}
